import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeUsPhone } from "@/lib/phone";
import type { ReadSource } from "@/lib/readSource";
import { api, apiProbe } from "@/v5/lib/api";
import {
  sendSMS,
  sendEmail as sendCommEmail,
  replyToCommLog,
} from "@/services/communications.service";

export interface CommlogEntry {
  commLogID: string;
  patientID: string;
  typeID: string;
  notes: string;
  dateAdded: string;
  date?: string;
  replyID?: string;
  smsMessage?: string;
  smsDirection?: string;
  emailSubject?: string;
  emailBody?: string;
  subject?: string;
  fromPatient?: string | boolean | number;
  authorFirstName?: string;
  authorLastName?: string;
  direction?: string;
  wasRead?: string | boolean | number;
  isStream?: boolean;
  surveyResponseId?: number | null;
  /**
   * Which of the practice's own numbers this text used, ten digits.
   *
   * Always the number THAT MESSAGE actually used. Absent when nothing was
   * recorded, which is the normal case for any text predating the change that
   * started recording it — nothing is ever inferred, because a guessed number
   * read out to a guest sends them to a line they never opted out of.
   */
  practiceNumber?: string;
  [key: string]: unknown;
}

export interface VoiceCallEntry {
  callStreamsID: string;
  createDate: string;
  callFrom: string;
  callTo: string;
  duration: string;
  summary: string;
  transcript?: string;
  archived: string;
  recordingUrl?: string | null;
  [key: string]: unknown;
}

export interface VideoCallEntry {
  videoCallID: string;
  roomSid: string;
  roomName: string;
  patientID: string;
  userID: string;
  duration: string;
  durationFormatted: string;
  compositionSid: string;
  compositionStatus: string;
  recordingUrl?: string | null;
  recordingMessage?: string | null;
  transcript?: string;
  createDate: string;
  endDate: string;
  [key: string]: unknown;
}

interface LaravelCommlogResponse {
  data: CommlogEntry[];
  meta: {
    total?: number;
    page?: number;
    perPage?: number;
    totalPages?: number;
    /**
     * Whether a plain WhatsApp message would reach this patient right now.
     * Absent or null means the server could not tell, which is NOT "closed".
     */
    whatsappWindowOpen?: boolean | null;
  };
}

const COMMLOG_PAGE_SIZE = 50;

/**
 * Whether to fire the Comm Log timing probe alongside a tab load.
 *
 * Off unless switched on, because the probe re-runs the same expensive queries
 * the tab just ran — armed for everyone it would double the cost of every
 * Comm Log open, on an endpoint that is already timing out.
 *
 * Two switches: `?profile=1` on the URL for a one-off look, or
 * `localStorage.setItem('ftp-commlog-profile', '1')` to keep it on across
 * navigations while chasing a bug across several patients.
 */
function commlogProfilingEnabled(): boolean {
  try {
    if (localStorage.getItem("ftp-commlog-profile") === "1") return true;
    return new URLSearchParams(window.location.search).get("profile") === "1";
  } catch {
    return false;
  }
}

/**
 * Drop a timing breakdown for this patient into the Network tab, so diagnosing
 * a slow Comm Log is "open the tab and look" rather than a console snippet.
 *
 * Fired AFTER the tab's own requests settle, never alongside them. Run
 * concurrently it would contend with them for the same Lambda and the same
 * connections and inflate precisely the timings it exists to measure — and it
 * would double peak load on a patient whose Comm Log is already at the 30s
 * ceiling.
 *
 * Why a separate request at all, when the Network tab already shows the real
 * ones: a request killed by the API Gateway timeout has no response body, so
 * the failing call can only ever show a bare 500. The probe carries its own
 * wall-clock budget and therefore returns something to read.
 */
function fireCommlogProfile(patientId: number | string): void {
  if (!commlogProfilingEnabled()) return;
  apiProbe(`/communications/comm-log/patient/${patientId}/diagnose`, {
    // Match what the tab actually requested: the per-row costs scale with it.
    pageSize: String(COMMLOG_PAGE_SIZE),
  });
}

interface SmsStreamEntry {
  smsStreamsID: string;
  message: string;
  direction: string;
  carrierStatus?: string;
  smsFrom: string;
  smsTo: string;
  messageTime: string;
  createDate?: string;
  patientID: string;
}

interface PatientStreamsResponse {
  data: {
    voiceCalls: VoiceCallEntry[];
    smsStreams: SmsStreamEntry[];
    videoCalls: VideoCallEntry[];
  };
}

/**
 * A marketing campaign / automation message sent to this patient.
 *
 * These are derived from the marketing send logs at read time — no marketing
 * send is ever written into comm_log — and are loaded separately from the
 * paginated comm log so they never compete for slots in a page.
 */
interface MarketingEmailEntry {
  sourceKind: string;
  sendID: string;
  sentAt: string;
  campaignName: string;
  subject: string;
  /** Inbox preview line; the only body text shown inline for a marketing email. */
  preheader: string;
  /**
   * The list endpoint carries no body — it is fetched per send when the
   * "View full email" dialog opens. True whenever one can be requested.
   */
  hasFullBody?: boolean;
  /**
   * @deprecated Only sent by an API predating the per-send body endpoint.
   * Retained so an older API keeps working; remove once none is deployed.
   */
  htmlContent?: string;
  deliveryStatus: string;
  recipient: string;
  campaignID: string;
  automationID: string;
  contactID: string;
  stepOrder: string;
}

interface MarketingSmsEntry {
  sourceKind: string;
  sendID: string;
  sentAt: string;
  campaignName: string;
  messageBody: string;
  deliveryStatus: string;
  recipient: string;
  campaignID: string;
  automationID: string;
  contactID: string;
  stepOrder: string;
}

/**
 * The patient asked for more information through a request-info link in a
 * marketing message, which created a lead. Derived read-only from the lead's
 * attribution record — nothing is written to comm_log — and returned beside
 * the marketing sends.
 */
interface MarketingRequestEntry {
  id: number;
  leadId: number;
  /** ISO 8601. */
  createdAt: string;
  /** '' for a bare link that no send delivered. */
  channel: "email" | "sms" | "";
  resourceName: string;
  sourceKind: "campaign" | "automation" | "link";
  sourceName: string | null;
  stepOrder: number | null;
  stepName: string | null;
  leadKind: "recipient" | "referral" | "generic";
  /** The lead row was hard-deleted since; there is nothing left to open. */
  leadDeleted: boolean;
}

interface PatientMarketingResponse {
  data: {
    marketingEmails: MarketingEmailEntry[];
    marketingSms: MarketingSmsEntry[];
    /** Absent from API builds that predate request-info links. */
    marketingRequests?: MarketingRequestEntry[];
  };
}

export interface HealthNexusMessage {
  id: string;
  body: string;
  senderType: string;
  senderName: string;
  createdAt: string;
}

interface HealthNexusResponse {
  data: {
    messages: HealthNexusMessage[];
    /** Whether the patient's portal chat room answered — gates the composer. */
    canSend?: boolean;
  };
}

function smsStreamToCommlogEntry(stream: SmsStreamEntry): CommlogEntry {
  const direction = String(stream.direction ?? "").toLowerCase();
  const fromPatient =
    direction.includes("inbound") ||
    direction.includes("incoming") ||
    direction.includes("received");

  return {
    commLogID: `sms-stream-${stream.smsStreamsID}`,
    patientID: String(stream.patientID ?? ""),
    typeID: "18",
    notes: String(stream.message ?? ""),
    dateAdded: String(stream.messageTime ?? stream.createDate ?? ""),
    date: String(stream.messageTime ?? stream.createDate ?? ""),
    fromPatient: fromPatient ? 1 : 0,
    wasRead: 1,
    direction: stream.direction,
    smsMessage: stream.message,
    smsDirection: stream.direction,
    isStream: true,
    smsStreamsID: stream.smsStreamsID,
    smsFrom: stream.smsFrom,
    smsTo: stream.smsTo,
    carrierStatus: stream.carrierStatus,
    // Stream rows have always carried both endpoints, so the practice number is
    // known for certain here and needs no fallback. Whichever side is not the
    // guest is ours: on an inbound message that is the To, on an outbound one
    // the From. Recorded rather than current, because these are the numbers
    // that message genuinely used.
    // normalizeUsPhone is the repo's existing helper and already applies the
    // same rule as the server's CommMessageNumberService::normalize, so a
    // stream row and a comm_log row cannot disagree about what is showable.
    // `?? undefined` only converts its null to the absent-field shape.
    practiceNumber: normalizeUsPhone(fromPatient ? stream.smsTo : stream.smsFrom) ?? undefined,
  };
}

/** Campaign name plus the step number for automations, e.g. "Welcome Series (step 2)". */
function marketingLabel(sourceKind: string, campaignName: string, stepOrder: string): string {
  const base =
    String(campaignName ?? "").trim() ||
    (sourceKind === "campaign" ? "Marketing campaign" : "Marketing automation");
  const step = Number(stepOrder ?? 0);
  return sourceKind === "automation" && step > 0 ? `${base} (step ${step})` : base;
}

/**
 * typeID 25 — marketing e-mail. Shows subject + preheader inline, with the full
 * campaign HTML behind the existing "View full message" dialog.
 *
 * `notes` deliberately carries ONLY the preheader. It must never hold the
 * campaign HTML: the preview pipeline strips tags via textContent, which
 * includes the contents of the template's <style> blocks, so the bubble filled
 * with literal CSS. plainContent is no good either — it is the entire email as
 * plain text, which is a wall of copy, not a preview.
 *
 * The body is normally not sent with the list: `emailBody` is left unset and
 * `hasFullBody` marks it for lazy loading — the same treatment a
 * server-truncated comm_log body already gets, which means the existing
 * "View full message" affordance works unchanged.
 *
 * BOTH response shapes are accepted, so the two sides never have to deploy in
 * lockstep. An API that still inlines the body is used as-is; only when no body
 * arrives do we fall back to lazy loading. Without this, shipping the API first
 * silently removed the "View full email" link from every marketing message
 * until the matching UI bundle went out.
 */
function marketingEmailToCommlogEntry(entry: MarketingEmailEntry, patientId: string): CommlogEntry {
  const label = marketingLabel(entry.sourceKind, entry.campaignName, entry.stepOrder);
  const subject = String(entry.subject ?? "").trim() || "No subject";
  const inlineBody = String(entry.htmlContent ?? "");

  return {
    // Parsed back apart by the lazy body loader to address the send — keep the
    // shape in step with parseMarketingCommLogId in ConversationThread.
    commLogID: `marketing-email-${entry.sourceKind}-${entry.sendID}`,
    patientID: patientId,
    typeID: "25",
    notes: String(entry.preheader ?? "").trim(),
    ...(inlineBody
      ? { emailBody: inlineBody }
      : { hasFullBody: entry.hasFullBody !== false }),
    emailSubject: subject,
    subject,
    dateAdded: String(entry.sentAt ?? ""),
    date: String(entry.sentAt ?? ""),
    // Marketing always goes out from the practice to the patient.
    fromPatient: 0,
    wasRead: 1,
    isStream: true,
    isMarketing: true,
    marketingChannel: "email",
    marketingSourceKind: String(entry.sourceKind ?? ""),
    marketingCampaignName: label,
    marketingDeliveryStatus: String(entry.deliveryStatus ?? ""),
    marketingRecipient: String(entry.recipient ?? ""),
  };
}

/** typeID 26 — marketing SMS. Short enough to render inline, no full-body dialog. */
function marketingSmsToCommlogEntry(entry: MarketingSmsEntry, patientId: string): CommlogEntry {
  const label = marketingLabel(entry.sourceKind, entry.campaignName, entry.stepOrder);

  return {
    commLogID: `marketing-sms-${entry.sourceKind}-${entry.sendID}`,
    patientID: patientId,
    typeID: "26",
    notes: String(entry.messageBody ?? ""),
    smsMessage: String(entry.messageBody ?? ""),
    subject: label,
    dateAdded: String(entry.sentAt ?? ""),
    date: String(entry.sentAt ?? ""),
    fromPatient: 0,
    wasRead: 1,
    isStream: true,
    isMarketing: true,
    marketingChannel: "sms",
    marketingSourceKind: String(entry.sourceKind ?? ""),
    marketingCampaignName: label,
    marketingDeliveryStatus: String(entry.deliveryStatus ?? ""),
    marketingRecipient: String(entry.recipient ?? ""),
  };
}

const PACIFIC_TZ = "America/Los_Angeles";

/**
 * An ISO timestamp as the Pacific wall-clock string every other entry in this
 * log carries ("YYYY-MM-DD HH:MM:SS", the API's config('app.timezone')).
 *
 * The thread reads timestamps as wall-clock text (normalizeTimestamp drops any
 * offset), so an ISO "…Z" passed through untouched would sit seven or eight
 * hours away from the campaign send it belongs next to. A string that already
 * has no offset is taken to be that wall clock and passed through.
 */
function isoToPacificWallClock(value: string): string {
  const raw = String(value ?? "").trim();
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return raw;
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return raw;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: PACIFIC_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(ms));
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "00";
    return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:${part("minute")}:${part("second")}`;
  } catch {
    return raw;
  }
}

/**
 * "Requested info: Botox Package · Spring Botox · step 3". The step appears
 * only for automations; a bare link has no source name, so it names only the
 * resource. A lead removed since is marked, and the entry then links nowhere.
 */
function marketingRequestLabel(entry: MarketingRequestEntry): string {
  const resource = String(entry.resourceName ?? "").trim() || "More information";
  const parts = [resource];
  const source = String(entry.sourceName ?? "").trim();
  if (source) parts.push(source);
  const step = Number(entry.stepOrder ?? 0);
  if (entry.sourceKind === "automation" && Number.isFinite(step) && step > 0) parts.push(`step ${step}`);
  const label = `Requested info: ${parts.join(" · ")}`;
  return entry.leadDeleted ? `${label} (deleted lead)` : label;
}

/**
 * typeID 27 — synthetic, like 25/26: the patient requested information through
 * a marketing link. It is the patient's own action, so it sits on the
 * patient's side of the thread; it is tagged with the channel the link arrived
 * on, and carries the lead id so the bubble can open the lead.
 */
function marketingRequestToCommlogEntry(entry: MarketingRequestEntry, patientId: string): CommlogEntry {
  const when = isoToPacificWallClock(String(entry.createdAt ?? ""));
  const channel = entry.channel === "email" || entry.channel === "sms" ? entry.channel : "";

  return {
    commLogID: `marketing-request-${entry.id}`,
    patientID: patientId,
    typeID: "27",
    notes: marketingRequestLabel(entry),
    dateAdded: when,
    date: when,
    fromPatient: 1,
    wasRead: 1,
    isStream: true,
    isMarketing: true,
    isMarketingRequest: true,
    marketingChannel: channel,
    marketingSourceKind: String(entry.sourceKind ?? ""),
    leadId: Number(entry.leadId ?? 0),
    leadDeleted: entry.leadDeleted === true,
  };
}

/** Oldest first, matching the thread's own sort; ties keep their order. */
function byTimestamp(a: CommlogEntry, b: CommlogEntry): number {
  const ms = (e: CommlogEntry) => {
    const t = Date.parse(String(e.dateAdded ?? "").replace(" ", "T"));
    return Number.isNaN(t) ? 0 : t;
  };
  return ms(a) - ms(b);
}

export function useCommlog(
  patientId: string | null,
  // Positional, so it cannot be removed without shifting every later
  // argument at the call sites. Underscored instead.
  _typeID: string = "all",
  patientPhone?: string,
  patientEmail?: string,
  /**
   * The screen this thread is on. A reply marks the message it answers as
   * read, and the read line shows where, so every reply request carries it.
   * Fresh messages do not: they mark nothing read.
   */
  readSource?: ReadSource,
) {
  // The timeline is assembled from three INDEPENDENT sources, held in separate
  // slices rather than one combined array so that a slow source can arrive late
  // without holding up the others. See refetch for why that matters.
  const [commlogEntries, setCommlogEntries] = useState<CommlogEntry[]>([]);
  const [streamEntries, setStreamEntries] = useState<CommlogEntry[]>([]);
  const [marketingEntries, setMarketingEntries] = useState<CommlogEntry[]>([]);
  const [voiceCalls, setVoiceCalls] = useState<VoiceCallEntry[]>([]);
  const [videoCalls, setVideoCalls] = useState<VideoCallEntry[]>([]);
  const [healthNexusEntries, setHealthNexusEntries] = useState<HealthNexusMessage[]>([]);
  const [healthNexusLoading, setHealthNexusLoading] = useState(false);
  const [healthNexusCanSend, setHealthNexusCanSend] = useState(false);
  const healthNexusRequestRef = useRef(0);
  // Same staleness guard the HealthNexus fetch uses: now that these two run on
  // their own clocks, a slow response must not repopulate a patient the user
  // has already navigated away from.
  const streamsRequestRef = useRef(0);
  const marketingRequestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Null until the first page answers, and whenever the server declines to say.
  const [whatsAppWindowOpen, setWhatsAppWindowOpen] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Concatenated in the same order the previous single-array version produced,
  // so the downstream timestamp sort behaves identically.
  const entries = useMemo(
    () => [...commlogEntries, ...streamEntries, ...marketingEntries],
    [commlogEntries, streamEntries, marketingEntries],
  );

  const fetchCommlogPage = useCallback(
    async (targetPage: number) => {
      if (!patientId) return null;
      // pageSize=50 keeps the response well under Lambda's 6MB payload
      // ceiling. Patients with long email threads (full HTML bodies in
      // notes) were blowing past it at 200, producing a bare
      // "Internal Server Error" before Laravel could format anything.
      const result = await api<LaravelCommlogResponse>(
        `/communications/comm-log/patient/${patientId}`,
        { params: { pageSize: String(COMMLOG_PAGE_SIZE), page: String(targetPage) } },
      );
      const totalPages = Number(result.meta?.totalPages ?? 0);
      return {
        entries: Array.isArray(result.data) ? result.data : [],
        hasMore: targetPage < totalPages,
        whatsAppWindowOpen: result.meta?.whatsappWindowOpen ?? null,
      };
    },
    [patientId],
  );

  /**
   * Call recordings and SMS streams. Independent of the main timeline.
   */
  const fetchStreams = useCallback(async () => {
    if (!patientId) return;
    const requestId = ++streamsRequestRef.current;
    const isStale = () => requestId !== streamsRequestRef.current;
    try {
      const res = await api<PatientStreamsResponse>(
        `/communications/comm-log/patient/${patientId}/streams`,
      );
      if (isStale()) return;
      setStreamEntries(
        Array.isArray(res.data?.smsStreams) ? res.data.smsStreams.map(smsStreamToCommlogEntry) : [],
      );
      setVoiceCalls(Array.isArray(res.data?.voiceCalls) ? res.data.voiceCalls : []);
      setVideoCalls(Array.isArray(res.data?.videoCalls) ? res.data.videoCalls : []);
    } catch (err) {
      if (isStale()) return;
      // Tolerated, as before: an account with no recordings still gets a
      // working Comm Log. It no longer costs the timeline anything.
      console.error("Failed to fetch commlog streams:", err);
      setStreamEntries([]);
      setVoiceCalls([]);
      setVideoCalls([]);
    }
  }, [patientId]);

  /**
   * Marketing sends, derived read-only from the campaign/automation send logs.
   * Unpaginated (they don't page) and independent of the main timeline.
   */
  const fetchMarketing = useCallback(async () => {
    if (!patientId) return;
    const requestId = ++marketingRequestRef.current;
    const isStale = () => requestId !== marketingRequestRef.current;
    try {
      const res = await api<PatientMarketingResponse>(
        `/communications/comm-log/patient/${patientId}/marketing`,
      );
      if (isStale()) return;
      setMarketingEntries(
        [
          ...(Array.isArray(res.data?.marketingEmails)
            ? res.data.marketingEmails.map((e) => marketingEmailToCommlogEntry(e, patientId))
            : []),
          ...(Array.isArray(res.data?.marketingSms)
            ? res.data.marketingSms.map((e) => marketingSmsToCommlogEntry(e, patientId))
            : []),
          // A request usually follows the send that carried its link by
          // minutes, so the slice is put in date order rather than appended.
          ...(Array.isArray(res.data?.marketingRequests)
            ? res.data.marketingRequests.map((e) => marketingRequestToCommlogEntry(e, patientId))
            : []),
        ].sort(byTimestamp),
      );
    } catch (err) {
      if (isStale()) return;
      // Tolerated, as before: an account without the marketing tables still
      // gets a working Comm Log.
      console.error("Failed to fetch commlog marketing:", err);
      setMarketingEntries([]);
    }
  }, [patientId]);

  /**
   * Load the timeline.
   *
   * Only the comm_log page is awaited. Streams and marketing are fired after it
   * and deliberately NOT awaited — the same treatment the HealthNexus chat
   * already gets, and for the same reason.
   *
   * These three used to share a Promise.all. Both extras were already
   * `.catch()`ed to empty, which made their failure non-fatal but NOT
   * non-blocking: Promise.all still waited for them, so a single slow side
   * request held the spinner for its full duration even though the timeline
   * itself had returned in milliseconds. With the marketing query unpaginated
   * and reading full campaign bodies, that ceiling is the platform's 30s
   * timeout — a blank tab for half a minute, then a render with marketing
   * silently missing. Now the timeline paints as soon as it arrives and the
   * extras merge in whenever they land, so the worst case is a complete tab
   * with late extras instead of an empty one.
   */
  const refetch = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const pageResult = await fetchCommlogPage(1);
      setCommlogEntries(pageResult?.entries ?? []);
      setPage(1);
      setHasMore(pageResult?.hasMore ?? false);
      setWhatsAppWindowOpen(pageResult?.whatsAppWindowOpen ?? null);
    } catch (err) {
      console.error("Failed to fetch commlog:", err);
      setError(err instanceof Error ? err.message : "Failed to load communication log");
      setHasMore(false);
    } finally {
      setLoading(false);
    }

    // Kicked off here rather than from their own effects so that every caller
    // that refreshes the log (a sent reply, for instance) still refreshes all
    // three sources, exactly as the Promise.all version did.
    void (async () => {
      await Promise.allSettled([fetchStreams(), fetchMarketing()]);
      // Only once everything has settled — the probe re-runs these same
      // queries, and firing it alongside them would inflate the very timings
      // it exists to measure. Runs on the failure path too, which is the path
      // worth measuring.
      fireCommlogProfile(patientId);
    })();
  }, [patientId, fetchCommlogPage, fetchStreams, fetchMarketing]);

  const loadMore = useCallback(async () => {
    if (!patientId || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const pageResult = await fetchCommlogPage(next);
      if (!pageResult) return;
      // Page N+1 contains older entries; prepend them so the downstream
      // timestamp sort still produces a correct timeline. (Stream entries
      // and voice/video calls are loaded once in refetch and don't paginate.)
      // Prepended to the comm_log slice only — the stream and marketing slices
      // are unpaginated and must not be disturbed by paging the timeline.
      setCommlogEntries((prev) => [...pageResult.entries, ...prev]);
      setPage(next);
      setHasMore(pageResult.hasMore);
    } catch (err) {
      console.error("Failed to load older commlog:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [patientId, page, hasMore, loadingMore, fetchCommlogPage]);

  useEffect(() => {
    // Clear all three slices, not just the timeline: the extras arrive on their
    // own clocks now, so a leftover slice would otherwise show the previous
    // patient's calls or campaigns until the new ones landed.
    setCommlogEntries([]);
    setStreamEntries([]);
    setMarketingEntries([]);
    setPage(1);
    setHasMore(false);
    refetch();
  }, [refetch]);

  const fetchHealthNexus = useCallback(async () => {
    if (!patientId) {
      setHealthNexusEntries([]);
      setHealthNexusCanSend(false);
      return;
    }
    // Ignore a response that lands after the patient changed (or after a newer
    // fetch was started), so a slow partner-API round trip can't repopulate the
    // previous patient's chat.
    const requestId = ++healthNexusRequestRef.current;
    const isStale = () => requestId !== healthNexusRequestRef.current;
    setHealthNexusLoading(true);
    try {
      const res = await api<HealthNexusResponse>(
        `/communications/comm-log/patient/${patientId}/healthnexus`,
      );
      if (isStale()) return;
      setHealthNexusEntries(Array.isArray(res.data?.messages) ? res.data.messages : []);
      setHealthNexusCanSend(res.data?.canSend === true);
    } catch (err) {
      if (isStale()) return;
      console.error("Failed to fetch HealthNexus chat:", err);
      setHealthNexusEntries([]);
      setHealthNexusCanSend(false);
    } finally {
      if (!isStale()) setHealthNexusLoading(false);
    }
  }, [patientId]);

  // HealthNexus chat is loaded LAZILY and INDEPENDENTLY of the main comm_log /
  // streams fetch above. It is intentionally NOT part of refetch's Promise.all
  // so the partner-API round trip can never delay the rest of the timeline —
  // messages simply merge in when (and if) they arrive.
  useEffect(() => {
    setHealthNexusEntries([]);
    setHealthNexusCanSend(false);
    void fetchHealthNexus();
  }, [fetchHealthNexus]);

  const sendReply = useCallback(
    async (
      commLogID: string,
      message: string,
      subject?: string,
      recipients?: string[],
    ) => {
      const body: Record<string, unknown> = { message };
      if (subject) body.subject = subject;
      if (recipients && recipients.length > 0) body.recipients = recipients;
      if (readSource) body.readSource = readSource;
      await api(`/communications/comm-log/${commLogID}/reply`, {
        method: "POST",
        body,
      });
      await refetch();
    },
    [readSource, refetch],
  );

  /**
   * Send an SMS, including a reply to an existing message in the thread.
   *
   * Replies must NOT go through sendReply(). The comm-log reply endpoint
   * dispatches email and records every other channel in the database without
   * sending it, so a reply typed into the SMS thread appeared in the log,
   * nested under its parent, behind a success toast -- while the patient
   * received nothing and no delivery status was ever written to say so.
   * Posting to the send endpoint with `replyId` threads the message AND
   * delivers it.
   *
   * A reply needs the patient's number for the same reason a new message does:
   * without one there is nowhere to send it, and the throw surfaces that in
   * the composer instead of resolving as a silent success.
   *
   * `fromNumber` is the composer's "Send from" choice. Without one the server
   * picks the number as it always has, so it is sent only when given.
   */
  const sendSms = useCallback(
    async (message: string, replyID?: string, recipients?: string[], fromNumber?: string) => {
      if (!patientId) return;
      if (!patientPhone) {
        throw new Error("No phone number available");
      }
      await sendSMS({
        patientId: Number(patientId),
        toPhone: patientPhone,
        message,
        ...(replyID ? { replyId: replyID } : {}),
        ...(replyID && readSource ? { readSource } : {}),
        ...(recipients && recipients.length > 0 ? { recipients } : {}),
        ...(fromNumber ? { fromNumber } : {}),
      });
      await refetch();
    },
    [patientId, patientPhone, readSource, refetch],
  );

  const sendEmail = useCallback(
    async (
      subject: string,
      message: string,
      replyID?: string,
      recipients?: string[],
      files?: File[],
      existingDocuments?: string[],
    ) => {
      if (!patientId) return;
      if (replyID) {
        // Email replies go through the multipart reply endpoint so the
        // message actually sends via Mandrill and carries attachments.
        await replyToCommLog(replyID, {
          message,
          subject,
          ...(recipients && recipients.length > 0 ? { recipients } : {}),
          ...(files && files.length > 0 ? { files } : {}),
          ...(existingDocuments && existingDocuments.length > 0 ? { existingDocuments } : {}),
          ...(readSource ? { readSource } : {}),
        });
        await refetch();
        return;
      }
      if (!patientEmail) {
        throw new Error("No email address available");
      }
      await sendCommEmail({
        patientId: Number(patientId),
        toEmail: patientEmail,
        subject,
        htmlContent: message,
        ...(recipients && recipients.length > 0 ? { recipients } : {}),
        ...(files && files.length > 0 ? { files } : {}),
        ...(existingDocuments && existingDocuments.length > 0 ? { existingDocuments } : {}),
      });
      await refetch();
    },
    [patientEmail, patientId, readSource, refetch],
  );

  const sendNote = useCallback(
    async (notes: string, replyID?: string, recipients?: string[], subject?: string) => {
      if (!patientId) return;
      if (replyID) {
        await sendReply(replyID, notes, undefined, recipients);
        return;
      }
      const body: Record<string, unknown> = { patientId, message: notes, type: 0 };
      if (subject) body.subject = subject;
      if (recipients && recipients.length > 0) body.recipients = recipients;
      await api("/communications/comm-log", { method: "POST", body });
      await refetch();
    },
    [patientId, refetch, sendReply],
  );

  /**
   * Send a WhatsApp message, including a reply to an existing one.
   *
   * Replies go to the send endpoint with `replyId` for the same reason SMS
   * does -- see sendSms above. The comm-log reply endpoint refuses both
   * channels outright now, so there is no route back to the
   * logged-but-never-sent behaviour.
   */
  const sendWhatsApp = useCallback(
    async (message: string, replyID?: string, recipients?: string[]) => {
      if (!patientId) return;
      if (!patientPhone) {
        throw new Error("No phone number available");
      }
      const body: Record<string, unknown> = {
        patientId,
        toPhone: patientPhone,
        message,
        channel: "whatsapp",
      };
      if (replyID) body.replyId = replyID;
      if (replyID && readSource) body.readSource = readSource;
      if (recipients && recipients.length > 0) body.recipients = recipients;
      await api("/communications/sms/send", { method: "POST", body });
      await refetch();
    },
    [patientId, patientPhone, readSource, refetch],
  );

  /**
   * Send one of the practice's approved WhatsApp messages.
   *
   * The only thing WhatsApp will deliver once the patient's reply window has
   * closed. Carries no free text of its own -- the approved message IS the
   * content, and the server refuses a request that tries to send both.
   */
  const sendWhatsAppTemplate = useCallback(
    async (contentSid: string, variables: Record<string, string>, recipients?: string[]) => {
      if (!patientId) return;
      if (!patientPhone) {
        throw new Error("No phone number available");
      }
      const body: Record<string, unknown> = {
        patientId,
        toPhone: patientPhone,
        channel: "whatsapp",
        contentSid,
        templateVariables: variables,
      };
      if (recipients && recipients.length > 0) body.recipients = recipients;
      await api("/communications/sms/send", { method: "POST", body });
      await refetch();
    },
    [patientId, patientPhone, refetch],
  );

  /**
   * Post a care-team message into the patient's HealthNexus portal chat. The
   * portal is the system of record, so nothing is appended locally — the
   * message arrives through the history refetch below.
   */
  const sendHealthNexus = useCallback(
    async (message: string) => {
      if (!patientId) return;
      // One id per logical message: the portal is idempotent on it, so a retry
      // of the same send can never post twice or double the unread count.
      const clientMsgId = `v5-${patientId}-${
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      }`;
      try {
        await api(`/communications/comm-log/patient/${patientId}/healthnexus`, {
          method: "POST",
          body: { body: message, clientMsgId },
        });
      } catch (err) {
        // The v5 fetch client prefixes thrown messages with "[status] "; strip
        // it so the composer shows the server's sentence on its own.
        const detail = err instanceof Error ? err.message.replace(/^\[\d+\]\s*/, "") : "";
        throw new Error(detail || "Failed to send message");
      }
      await fetchHealthNexus();
    },
    [fetchHealthNexus, patientId],
  );

  return {
    entries,
    voiceCalls,
    videoCalls,
    healthNexusEntries,
    healthNexusLoading,
    healthNexusCanSend,
    loading,
    loadingMore,
    error,
    hasMore,
    whatsAppWindowOpen,
    refetch,
    loadMore,
    refetchHealthNexus: fetchHealthNexus,
    sendSms,
    sendWhatsAppTemplate,
    sendEmail,
    sendNote,
    sendWhatsApp,
    sendHealthNexus,
  };
}
